<template>
  <template v-if="action.hasPermission">
    <Button
      @click="handleOpenNewRecurringTask(true)"
      variant="default"
      :label="action.label"
      size="2xl"
      class="flex h-14 p-4 pl-5 items-center justify-end gap-2.5 rounded-r-0 shadow-3xl"
      :aria-label="action.ariaLabel"
    >
      <template #start>
        <Icon name="plus" aria-hidden size="xl" />
      </template>
    </Button>
    <NewRecurringTask
      :open="isNewRecurringTaskPage"
      @close="handleOpenNewRecurringTask(false)"
      @save="handleSaveRecurringTask"
      ref="modalRef"
    />
  </template>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import { router } from "@inertiajs/vue3";
import { useI18n } from "vue-i18n";
import { isEmpty } from "lodash";
import { toUTC } from "@/Utils/date";
import { Button } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useSites } from "@/Composables/useSites";
import NewRecurringTask from "@/Tailwind/Components/Tasks/NewRecurringTask.vue";

// Emitter
const emitter = inject("emitter");

// Translations
const { t } = useI18n();

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Sites
const { isLoading, data: buildings } = useSites();

const isNewRecurringTaskPage = computed(() => {
  return usePage().url.includes(
    "admin/settings/organisation/recurring-tasks/new"
  );
});

const modalRef = ref(undefined);

const action = computed(() => ({
  label: t("admin.recurring_tasks.label", 1),
  ariaLabel: t("components.buttons.add_new_recurring_task"),
  component: undefined,
  hasPermission: canCreateRecurringTasks.value,
}));

const canCreateRecurringTasks = computed(() => {
  if (isLoading.value) {
    return false;
  }

  return !isEmpty(
    buildings.value.filter((building) =>
      hasPermission(permissions.createRecurringTask, building.id)
    )
  );
});

function handleSaveRecurringTask(task) {
  router.post(
    route("settings.organisation.recurring-tasks.store"),
    {
      ...task,
      date_scheduled: task.date_scheduled
        ? toUTC(task.date_scheduled.toISOString())
        : null,
      photos: task.photos.map((photo) => photo.data),
      rrule: task.rrule.toString(),
    },
    {
      onSuccess: () => {
        modalRef.value?.reset();
        emitter.emit("reload-recurring_tasks");
        router.visit(route("settings.organisation.recurring-tasks.index"));
      },
    }
  );
}

function handleOpenNewRecurringTask(value) {
  let url = route("settings.organisation.recurring-tasks.add");

  if (!value) {
    url = route("settings.organisation.recurring-tasks.index");
  }

  router.visit(url, {
    preserveState: true,
    preserveScroll: true,
  });
}
</script>
